import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AuthScaffold } from './AuthScaffold'
import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxSpinner } from '@/components/fx'
import { ApiError } from '@/lib/api'

/**
 * Where `POST /v1/staff/users/:uid/impersonate` sends a staff member's new
 * tab: `/auth/impersonate#code=<one-time code>`.
 *
 * The code lives in the URL **fragment**, not a query string — it never
 * reaches the gateway's access log or `Referer` header on the way here. It
 * is exchanged exactly once (the guard ref, same reasoning as
 * `OAuthCallbackPage`: React 18 StrictMode runs effects twice in dev, and a
 * consumed code's second exchange would fail on a sign-in that actually
 * worked).
 */
export function ImpersonatePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { beginImpersonation } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const exchanged = useRef(false)

  useEffect(() => {
    if (exchanged.current) return
    exchanged.current = true

    const code = new URLSearchParams(location.hash.replace(/^#/, '')).get('code')
    if (!code) {
      setError('Der Link ist unvollständig — es fehlt der Code.')
      return
    }

    void (async () => {
      try {
        await beginImpersonation(code)
        navigate('/', { replace: true })
      } catch (cause) {
        setError(ApiError.from(cause).message)
      }
    })()
  }, [location.hash, beginImpersonation, navigate])

  if (error) {
    return (
      <AuthScaffold title="Nutzeransicht nicht möglich">
        <FxAlert tone="danger">{error}</FxAlert>
      </AuthScaffold>
    )
  }

  return (
    <AuthScaffold title="Nutzeransicht wird eingerichtet">
      <FxSpinner label="Wird geladen …" />
    </AuthScaffold>
  )
}
